import './Plant.css';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';


function UserLogged() {
  const navigate = useNavigate();
  const [username] = useState(() => localStorage.getItem('username') || '');
  const [plants, setPlants] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [productionData, setProductionData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [productionLoading, setProductionLoading] = useState(false);
  const [selectedPlantId, setSelectedPlantId] = useState(null);
  const [allPlantsProductionData, setAllPlantsProductionData] = useState({});
  const [energySettings, setEnergySettings] = useState({
    time_slot_1: { start_time: '05:00', end_time: '11:00', power: 10, weekdays: [1,1,1,1,1,0,0], time_enable: '1', timemode: '1' },
    time_slot_2: { start_time: '20:00', end_time: '21:00', power: 50, weekdays: [1,1,1,1,1,0,0], time_enable: '1', timemode: '1' },
    time_slot_3: { start_time: '00:00', end_time: '06:00', power: 30, weekdays: [1,1,1,1,1,0,0], time_enable: '1', timemode: '0' },
    time_slot_4: { start_time: '14:00', end_time: '16:00', power: 20, weekdays: [1,1,1,1,1,0,0], time_enable: '1', timemode: '0' },
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [executionSummary, setExecutionSummary] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [inverterSettingsLoading, setInverterSettingsLoading] = useState(false);

  const handleAddPlant = () => {
    navigate('/addplant');
  };

  const updateEnergySettings = (plantId, timeSlot, settings) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    setSettingsLoading(true);

    const payload = {
      plant_id: plantId,
      time_slot: timeSlot,
      start_time: settings.start_time,
      end_time: settings.end_time,
      power: settings.power,
      weekdays: settings.weekdays,
      time_enable: settings.time_enable,
      timemode: settings.timemode,
    };

    fetch(`${API_BASE}/update-energy-settings`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Failed to update energy settings');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        alert(`Energy settings updated successfully for time slot ${timeSlot}`);
      })
      .catch((err) => {
        console.error('Error updating energy settings:', err);
        alert(`Error updating energy settings: ${err.message}`);
      })
      .finally(() => setSettingsLoading(false));
  };

  const executeEnergyManagement = (plantId) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    if (!window.confirm(`Execute energy management for plant ${plantId}?`)) {
      return;
    }

    setExecutionSummary(null);
    setSettingsLoading(true);

    fetch(`${API_BASE}/execute-energy-management`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plant_id: plantId }),
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Failed to execute energy management');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        setExecutionSummary({ ...data, plant_id: plantId });
      })
      .catch((err) => {
        console.error('Error executing energy management:', err);
        alert(`Error executing energy management: ${err.message}`);
      })
      .finally(() => setSettingsLoading(false));
  };

  const handleWeekdayChange = (timeSlotKey, dayIndex, value) => {
    const updatedSettings = { ...energySettings };
    const weekdaysCopy = [...updatedSettings[timeSlotKey].weekdays];
    weekdaysCopy[dayIndex] = value ? 1 : 0;
    updatedSettings[timeSlotKey].weekdays = weekdaysCopy;
    setEnergySettings(updatedSettings);
  };

  const fetchDailyProduction = (plantId) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    setProductionLoading(true);
    setSelectedPlantId(plantId);

    fetch(`${API_BASE}/daily-production?plant_id=${plantId}`, {
      method: 'GET',
      headers,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Failed to fetch production data');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        setProductionData(data);
        // Also fetch inverter settings when plant is selected
        fetchInverterEnergySettings(plantId);
      })
      .catch((err) => {
        console.error('Error fetching production data:', err);
        setProductionData(null);
      })
      .finally(() => setProductionLoading(false));
  };

  const fetchAllPlantsInverterSettings = (plantList) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch inverter settings for all plants in parallel
    const fetchPromises = plantList.map((plant) =>
      fetch(`${API_BASE}/inverter-energy-settings?plant_id=${plant.plant_id}`, {
        method: 'GET',
        headers,
      })
        .then(async (res) => {
          if (!res.ok) {
            console.warn(`Failed to fetch settings for plant ${plant.plant_id}`);
            return null;
          }
          return res.json();
        })
        .catch((err) => {
          console.error(`Error fetching settings for plant ${plant.plant_id}:`, err);
          return null;
        })
    );

    Promise.all(fetchPromises)
      .then((results) => {
        const settingsMap = {};
        plantList.forEach((plant, index) => {
          if (results[index] && results[index].settings) {
            settingsMap[plant.plant_id] = results[index].settings;
          }
        });
        console.log('All plant inverter settings loaded:', settingsMap);
      })
      .catch((err) => {
        console.error('Error fetching all plants inverter settings:', err);
      });
  };

  const fetchAllPlantsProduction = (plantList) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch production data for all plants in parallel
    const fetchPromises = plantList.map((plant) =>
      fetch(`${API_BASE}/daily-production?plant_id=${plant.plant_id}`, {
        method: 'GET',
        headers,
      })
        .then(async (res) => {
          const contentType = res.headers.get('content-type') || '';
          if (!res.ok) {
            return null;
          }

          if (contentType.includes('application/json')) {
            return res.json();
          }
          const txt = await res.text();
          try {
            return JSON.parse(txt);
          } catch (e) {
            return null;
          }
        })
        .catch((err) => {
          console.error(`Error fetching production data for plant ${plant.plant_id}:`, err);
          return null;
        })
    );

    Promise.all(fetchPromises)
      .then((results) => {
        const productionMap = {};
        plantList.forEach((plant, index) => {
          if (results[index]) {
            productionMap[plant.plant_id] = results[index];
          }
        });
        setAllPlantsProductionData(productionMap);

        // Set the first plant's data as the default selection
        if (plantList.length > 0 && results[0]) {
          setProductionData(results[0]);
          const firstPlantId = plantList[0].plant_id;
          setSelectedPlantId(firstPlantId);

          // Also fetch inverter settings for the first plant
          console.log(`Auto-fetching inverter settings for first plant: ${firstPlantId}`);
          fetchInverterEnergySettings(firstPlantId);
        }
      })
      .catch((err) => {
        console.error('Error fetching all plants production data:', err);
      });
  };

  const fetchInverterEnergySettings = (plantId) => {
    const token = localStorage.getItem('access_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    setInverterSettingsLoading(true);

    fetch(`${API_BASE}/inverter-energy-settings?plant_id=${plantId}`, {
      method: 'GET',
      headers,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Failed to fetch inverter settings');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        console.log('Raw inverter response:', data);
        console.log('Response structure - status:', data?.status);
        console.log('Response structure - settings type:', typeof data?.settings);
        console.log('Response structure - settings length:', data?.settings?.length);

        if (data?.settings?.[0]) {
          console.log('First setting structure:', data.settings[0]);
          console.log('First setting keys:', Object.keys(data.settings[0]));
        }

        // Parse inverter settings and populate energySettings
        if (data && data.settings && Array.isArray(data.settings) && data.settings.length > 0) {
          const newSettings = { ...energySettings };
          let updatedCount = 0;

          data.settings.forEach((setting, index) => {
            console.log(`%cProcessing TimeMode${index + 1}`, 'color: blue; font-weight: bold', setting);

            if (!setting) {
              console.warn(`TimeMode${index + 1} is null or undefined`);
              return;
            }

            // Handle both direct data and nested data.data structure
            const inverterData = setting.data || setting;

            if (inverterData && typeof inverterData === 'object') {
              const slotKey = `time_slot_${index + 1}`;

              // Parse time with fallback
              const startTime = (inverterData.timestarttime || inverterData.start_time || newSettings[slotKey].start_time || '').substring(0, 5);
              const endTime = (inverterData.timeendtime || inverterData.end_time || newSettings[slotKey].end_time || '').substring(0, 5);
              const power = parseInt(inverterData.timepower || inverterData.power || newSettings[slotKey].power) || newSettings[slotKey].power;
              const timeEnable = String(inverterData.time_enable !== undefined ? inverterData.time_enable : newSettings[slotKey].time_enable);
              const timeMode = String(inverterData.timemode !== undefined ? inverterData.timemode : newSettings[slotKey].timemode);

              newSettings[slotKey] = {
                start_time: startTime,
                end_time: endTime,
                power: power,
                weekdays: [
                  inverterData.timeweekday1 || inverterData.weekday1 ? 1 : 0,
                  inverterData.timeweekday2 || inverterData.weekday2 ? 1 : 0,
                  inverterData.timeweekday3 || inverterData.weekday3 ? 1 : 0,
                  inverterData.timeweekday4 || inverterData.weekday4 ? 1 : 0,
                  inverterData.timeweekday5 || inverterData.weekday5 ? 1 : 0,
                  inverterData.timeweekday6 || inverterData.weekday6 ? 1 : 0,
                  inverterData.timeweekday7 || inverterData.weekday7 ? 1 : 0,
                ],
                time_enable: timeEnable,
                timemode: timeMode,
              };

              console.log(`%c✅ Updated ${slotKey}`, 'color: green; font-weight: bold', newSettings[slotKey]);
              updatedCount++;
            } else {
              console.warn(`TimeMode${index + 1} data is not a valid object:`, setting);
            }
          });

          if (updatedCount > 0) {
            setEnergySettings(newSettings);
            console.log('%c✅ All inverter settings loaded successfully!', 'color: green; font-weight: bold; font-size: 14px', newSettings);
          } else {
            console.warn('No settings were successfully parsed');
          }
        } else {
          console.warn('No valid settings received from API - data structure:', data);
        }
      })
      .catch((err) => {
        console.error('❌ Error fetching inverter settings:', err);
        console.error('Error details:', err.message);
        // Keep default settings if fetch fails
      })
      .finally(() => setInverterSettingsLoading(false));
  };

  useEffect(() => {
    // Assumption: after login the frontend stores JWT in localStorage under 'access_token'.
    const token = localStorage.getItem('access_token');
    console.log('Token from localStorage:', token);
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Authorization header set:', headers);
    } else {
      console.warn('No token found in localStorage');
    }

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch plants
    fetch(`${API_BASE}/user/plants`, {
      method: 'GET',
      headers,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Failed to fetch plants');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        setPlants(data.plants || []);
        // Automatically fetch production data and inverter settings for all plants
        if (data.plants && data.plants.length > 0) {
          fetchAllPlantsProduction(data.plants);
          fetchAllPlantsInverterSettings(data.plants);
        }
      })
      .catch((err) => {
        console.error('Error fetching plants:', err);
      })
      .finally(() => setLoading(false));

    // Fetch energy prices
    fetch(`${API_BASE}/energy-prices`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          let bodyText = '';
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              bodyText = JSON.stringify(errJson);
            } else {
              bodyText = await res.text();
            }
          } catch (e) {
            bodyText = 'Unable to parse error response';
          }
          throw new Error(bodyText || 'Błąd podczas pobierania cen energii');
        }

        if (contentType.includes('application/json')) {
          return res.json();
        }
        const txt = await res.text();
        try {
          return JSON.parse(txt);
        } catch (e) {
          throw new Error('Expected JSON but got: ' + txt);
        }
      })
      .then((data) => {
        // Transform data for chart
        const transformedPrices = (data.prices || []).map((price) => ({
          time: price.dtime ? price.dtime.substring(11, 16) : 'N/A',
          rce_pln: price.rce_pln ? parseFloat(price.rce_pln) : 0,
          period: price.period,
          fullTime: price.dtime,
        }));
        setPrices(transformedPrices);
      })
      .catch((err) => {
        console.error('Error fetching energy prices:', err);
      })
      .finally(() => setPricesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="user-logged-page">
      <img
        src={require('./images/pv_m.png')}
        alt="PV Banner"
        className="image-main"
      />
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        {username && (
          <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>
            Logged in as: {username}
          </p>
        )}
        <h1>Twoje Instalacje</h1>
        <p>
          Ta aplikacja ma na celu umożliwienie maksymalizacji Twojego zysku. 
          Z instalacji fotowoltaicznej z magazynowaniem energii.
        </p>
      </div>

      {/* Energy Prices Dashboard */}
      {!pricesLoading && prices.length > 0 && (
        <div className="price-dashboard">
          <h2>Rynkowa cena energii elektrycznej (RCE) - {prices[0]?.fullTime?.substring(0, 10)}</h2>
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prices} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(118, 75, 162, 0.2)" />
                <XAxis
                  dataKey="time"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fill: '#2E7D32', fontSize: 12 }}
                />
                <YAxis
                  label={{ value: 'zł/MWh', angle: -90, position: 'insideLeft', fill: '#2E7D32' }}
                  tick={{ fill: '#2E7D32' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '2px solid #2E7D32',
                    borderRadius: '8px',
                    color: '#2E7D32'
                  }}
                  formatter={(value) => [`${value.toFixed(2)} zł/MWh`, 'RCE']}
                />
                <Legend wrapperStyle={{ paddingTop: '20px', color: '#2E7D32' }} />
                <Bar
                  dataKey="rce_pln"
                  fill="#4CAF50"
                  name="RCE"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="energy-settings-section">
        {loading && <p>Loading plants...</p>}

        {!loading && plants.length === 0 && <p>No plants found. Add one using the button below.</p>}

        <div className="plants-grid">
          {plants.map((p) => {
            const plantProduction = allPlantsProductionData[p.plant_id];
            return (
            <div className="plant-card" key={p.id}>
              <h3>{p.plant_id || p.serial_number || `Plant ${p.id}`}</h3>
              <p><strong>Serial:</strong> {p.serial_number}</p>
              <p><strong>Model:</strong> {p.model}</p>
              <p><strong>City:</strong> {p.city}</p>
              <p><strong>Battery (kWh):</strong> {p.battery_storage ?? 'N/A'}</p>
              <p><strong>Installed:</strong> {p.installation_date || 'N/A'}</p>

              {plantProduction && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <p><strong>Today's Production:</strong> {plantProduction.production?.pvkwh?.toFixed(2) || 'N/A'} kWh</p>
                </div>
              )}

              <button
                onClick={() => fetchDailyProduction(p.plant_id)}
                style={{
                  marginTop: '15px',
                  padding: '8px 16px',
                  backgroundColor: selectedPlantId === p.plant_id ? '#2E7D32' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {selectedPlantId === p.plant_id ? '✓ Selected' : 'View Details'}
              </button>
            </div>
            );
          })}
        </div>

        {/* Daily Production Data Table */}
        {productionData && productionData.production && (
          <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <h2>Daily Energy Production - {productionData.date}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#333', color: 'white' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Metric</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Value (kWh)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>PV Production</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.pvkwh?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>Grid Consumption</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.kwhac?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>Total Load</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.load?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>Load from Battery</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.load_from_bat?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>Load from Grid</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.load_from_grid?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>Load from PV</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.load_from_pv?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>PV to Battery</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.pv_to_bat?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>PV to Grid</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.pv_to_grid?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px' }}><strong>PV to Load</strong></td>
                    <td style={{ padding: '12px' }}>{productionData.production.pv_to_load?.toFixed(2) || 'N/A'}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#e8f4f8', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
                    <td style={{ padding: '12px' }}>Balance</td>
                    <td style={{ padding: '12px', color: productionData.production.balance < 0 ? '#d32f2f' : '#388e3c' }}>
                      {productionData.production.balance?.toFixed(2) || 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Energy Management Settings */}
        {selectedPlantId && (
          <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Energy Management Settings - {selectedPlantId}</h2>
              <button
                onClick={() => executeEnergyManagement(selectedPlantId)}
                disabled={settingsLoading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ff9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: settingsLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  marginLeft: '10px'
                }}
                title="Execute energy management optimization"
              >
                {settingsLoading ? '⏳ Executing...' : '🚀 Execute Energy Management'}
              </button>
            </div>
            {executionSummary && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #81c784',
                  borderRadius: '8px',
                }}
              >
                <h3 style={{ marginTop: 0 }}>Execution summary</h3>
                <p><strong>Plant:</strong> {executionSummary.plant_id}</p>
                <p>
                  <strong>Predicted battery loading time:</strong>{' '}
                  {executionSummary.predicted_loading_hours} hours
                </p>
                <p>
                  <strong>Result:</strong> {executionSummary.result}
                </p>
                <p>
                  <strong>Discharging:</strong>{' '}
                  {executionSummary.discharging_disabled
                    ? 'Disabled (morning and evening discharging were switched off).'
                    : `Not switched off; scheduled to start at ${String(executionSummary.discharging_start_hour).padStart(2, '0')}:00.`}
                </p>
                {!executionSummary.discharging_disabled && (
                  <p>
                    <strong>Charging:</strong> Scheduled to start at{' '}
                    {String(executionSummary.charging_start_hour).padStart(2, '0')}:00.
                  </p>
                )}
              </div>
            )}
            <div className="energy-settings-grid">
              {[1, 2, 3, 4].map((slotNum) => {
                const slotKey = `time_slot_${slotNum}`;
                const settings = energySettings[slotKey];
                const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

                return (
                  <div key={slotNum} style={{
                    backgroundColor: settings.time_enable === '1' ? 'white' : '#e8e8e8',
                    padding: '15px',
                    borderRadius: '8px',
                    border: settings.time_enable === '1' ? '1px solid #ddd' : '1px solid #bbb',
                    opacity: settings.time_enable === '1' ? 1 : 0.6,
                    transition: 'all 0.3s ease'
                  }}>
                    <h3 style={{ margin: '0 0 15px 0', color: settings.time_enable === '1' ? '#2E7D32' : '#999' }}>
                      Time Slot {slotNum}
                      {settings.time_enable === '0' && <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 'normal' }}>(Inactive)</span>}
                    </h3>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Start Time:
                      </label>
                      <input
                        type="time"
                        value={settings.start_time}
                        onChange={(e) => setEnergySettings({
                          ...energySettings,
                          [slotKey]: { ...settings, start_time: e.target.value }
                        })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        End Time:
                      </label>
                      <input
                        type="time"
                        value={settings.end_time}
                        onChange={(e) => setEnergySettings({
                          ...energySettings,
                          [slotKey]: { ...settings, end_time: e.target.value }
                        })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Mode:
                      </label>
                      <select
                        value={settings.timemode}
                        onChange={(e) => setEnergySettings({
                          ...energySettings,
                          [slotKey]: { ...settings, timemode: e.target.value }
                        })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="0">🔋 Charging (Ładowanie)</option>
                        <option value="1">⚡ Discharging (Rozładowywanie)</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Power Level: {settings.power.toFixed(1)}%
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="100"
                        step="0.1"
                        value={settings.power}
                        onChange={(e) => setEnergySettings({
                          ...energySettings,
                          [slotKey]: { ...settings, power: parseFloat(e.target.value) }
                        })}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Active Days:
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
                        {weekdayLabels.map((label, index) => (
                          <label key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input
                              type="checkbox"
                              checked={settings.weekdays[index] === 1}
                              onChange={(e) => handleWeekdayChange(slotKey, index, e.target.checked)}
                            />
                            <span style={{ fontSize: '12px' }}>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={settings.time_enable === '1'}
                          onChange={(e) => setEnergySettings({
                            ...energySettings,
                            [slotKey]: { ...settings, time_enable: e.target.checked ? '1' : '0' }
                          })}
                        />
                        <span style={{ fontWeight: 'bold' }}>Enable this time slot</span>
                      </label>
                    </div>

                    <button
                      onClick={() => updateEnergySettings(selectedPlantId, slotNum, settings)}
                      disabled={settingsLoading}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#2E7D32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {settingsLoading ? 'Updating...' : `Update Slot ${slotNum}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add Plant Button at Bottom */}
      <div className="add-plant-button-container">
        <button
          onClick={handleAddPlant}
          className='add-plant-button'
          title="Add Plant"
        >
          <span>+</span>
          <span>Add Plant</span>
        </button>
      </div>
    </div>
  );
}

export default UserLogged;
